import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  TextField,
} from "@material-ui/core";
import { createContainerRecord, updateContainerRecord } from "api/purchase";
import { Checkbox, DatePicker, TextInput } from "components/Form";
import { useLayoutDispatch, popupNotification } from "context/LayoutContext";
import getClearAdornment from "utils/form";

import { X as Close, Receipt, ClipboardList } from "icons";

import useStyles from "../containers/styles";
import { blueGrey } from "@material-ui/core/colors";

const initialParams = {
  po_type: "Mix",
  po_number: "",
  container_number: "",
  bol_number: "",
  eta: "",
  ata: "",
  receipt_uploaded: false,
  seller_id: 2,
  loading_method: "Floor_Loaded",
};

const CreateModal = ({ open, onClose, onCreate, onEdit, editContainer, t }) => {
  const classes = useStyles();
  const [params, setParams] = useState(initialParams);

  const [isLoading, setIsLoading] = useState(false);
  const layoutDispatch = useLayoutDispatch();

  useEffect(() => {
    if (editContainer) {
      setParams({
        ...editContainer,
      });
    } else {
      setParams(initialParams);
    }
  }, [editContainer]);

  const handleCreate = () => {
    setIsLoading(true);
    createContainerRecord({
      setError: (e) => {
        setIsLoading(false);
        popupNotification({
          dispatch: layoutDispatch,
          message: e?.message || "An error occurred!",
        });
      },
      setFunc: () => {
        setIsLoading(false);
        onClose();
        onCreate();
        setParams(initialParams);
        popupNotification({
          dispatch: layoutDispatch,
          message: "Container record created.",
          status: "success",
        });
      },
      body: params,
    });
  };

  const handleEdit = () => {
    setIsLoading(true);
    updateContainerRecord({
      setError: (e) => {
        setIsLoading(false);
        popupNotification({
          dispatch: layoutDispatch,
          message: e?.message || "An error occurred!",
        });
      },
      setFunc: () => {
        setIsLoading(false);
        onClose();
        onEdit();
        setParams(initialParams);
        popupNotification({
          dispatch: layoutDispatch,
          message: "Container record updated.",
          status: "success",
        });
      },
      body: params,
      id: editContainer.id,
    });
  };

  const handleChangeCheckbox = (prop) => (event) => {
    setParams((prev) => ({ ...prev, [prop]: !prev[prop] }));
  };

  const handleChangeFilterInput = (prop) => ({ target: { value } }) => {
    setParams((prev) => ({ ...prev, [prop]: value }));
  };

  const clearHandler = (input) => {
    setParams({ ...params, [input]: "" });
  };

  const clearAdornment = (input) => {
    getClearAdornment(input, clearHandler, params);
  };

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      scroll="paper"
      open={open}
      keepMounted
      aria-labelledby="item-label-dialog-title"
      onClose={(_, reason) => {
        if (reason === "backdropClick") {
          onClose();
        }
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: blueGrey[100],
          padding: "10px",
        }}
      >
        <DialogTitle
          id="item-label-dialog-title"
          className={classes.dialogTitle}
          color="primary"
        >
          {`${editContainer ? "Update" : "Create"} Container Record`}
        </DialogTitle>
        <Button
          size="small"
          onClick={() => {
            onClose();
          }}
        >
          <Close color="black" />
        </Button>
      </div>

      <DialogContent className={classes.noGutter}>
        <Paper square variant="elevation">
          <Box style={{ width: "100%", padding: 20 }}>
            <Grid
              container
              noGutter={true}
              spacing={2}
              className={classes.mt10}
            >
              <Grid item xs={12} sm={6} md={4} lg={4}>
                <TextInput
                  id="container_number"
                  label={t("container_number")}
                  variant="outlined"
                  fullWidth
                  value={params.container_number}
                  onChange={handleChangeFilterInput("container_number")}
                  InputProps={clearAdornment("container_number")}
                  LeftIcon={Receipt}
                  hasBorder
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={4}>
                <TextInput
                  id="po_number"
                  label={t("po_number")}
                  variant="outlined"
                  fullWidth
                  value={params.po_number}
                  onChange={handleChangeFilterInput("po_number")}
                  InputProps={clearAdornment("po_number")}
                  LeftIcon={Receipt}
                  hasBorder
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={4}>
                <TextInput
                  id="bol_number"
                  label={t("bol_number")}
                  variant="outlined"
                  fullWidth
                  value={params.bol_number}
                  onChange={handleChangeFilterInput("bol_number")}
                  InputProps={clearAdornment("bol_number")}
                  LeftIcon={Receipt}
                  hasBorder
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={4}>
                <Checkbox
                  checked={params.is_released}
                  onChange={handleChangeCheckbox("is_released")}
                  color="blue"
                  label={`Released`}
                />
              </Grid>
            </Grid>
            <Divider className={classes.divider} />
            <Grid container noGutter={true} spacing={2}>
              <Grid item xs={12} lg={4}>
                <DatePicker
                  id="eta"
                  title="ETA"
                  value={params.eta}
                  onChange={handleChangeFilterInput("eta")}
                  hasBorder
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <DatePicker
                  id="ata"
                  title="ATA"
                  value={params.ata}
                  onChange={handleChangeFilterInput("ata")}
                  hasBorder
                />
              </Grid>
              <Grid item xs={4}>
                <Grid container>
                  <Grid item xs={2}>
                    <div className={classes.notesLabelContainer}>
                      <ClipboardList color="rgb(10, 182, 255)" />
                      <span className={classes.notesLabel}>Notes:</span>
                    </div>
                  </Grid>
                  <Grid item xs={10} className={classes.notesInputContainer}>
                    <TextField
                      id="notes"
                      variant="outlined"
                      fullWidth
                      value={params.notes}
                      onChange={handleChangeFilterInput("notes")}
                      InputProps={{
                        className: classes.notesInput,
                      }}
                      LeftIcon={ClipboardList}
                      multiline
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              alignItems="flex-end"
              justifyContent="center"
              className={classes.mt10}
            >
              <Grid item xs={12}>
                <Button
                  onClick={editContainer ? handleEdit : handleCreate}
                  variant="contained"
                  color="secondary"
                  size="small"
                  className={classes.actionButton}
                  disabled={isLoading || !params.container_number}
                >
                  {isLoading ? (
                    <CircularProgress size={25} />
                  ) : editContainer ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default CreateModal;
